import type { ToolOption } from '@/modules/Editor/tools/types'
import { MaskBrushDimension } from '@/modules/Editor/tools/types'
import { ToolName, SubToolName } from '@/modules/Editor/tools/types'

export interface ToolConfig {
  name: ToolName
  sub: false
  toolOptions?: ToolOption[]
}

export interface SubToolConfig {
  name: SubToolName
  sub: true
  toolOptions?: ToolOption[]
}

export const editToolConfig: ToolConfig = {
  name: ToolName.Edit,
  sub: false,
  toolOptions: [
    { id: 'restore_joints', active: false, props: {}, action: 'edit_tool.restore_joints' },
    {
      id: 'polygon_merge',
      active: false,
      props: {},
      category: 'polygon-bool',
      action: 'edit_tool.activate_polygon_merge',
    },
    {
      id: 'polygon_subtract',
      active: false,
      props: {},
      category: 'polygon-bool',
      action: 'edit_tool.activate_polygon_subtract',
    },
    {
      id: 'polygon_slice',
      active: false,
      props: {},
      category: 'polygon-bool',
      action: 'edit_tool.activate_polygon_slice',
    },
  ],
}

export const boundingBoxToolConfig: ToolConfig = {
  name: ToolName.BoundingBox,
  sub: false,
}

export const keypointToolConfig: ToolConfig = {
  name: ToolName.Keypoint,
  sub: false,
}

export const polygonToolConfig: ToolConfig = {
  name: ToolName.Polygon,
  sub: false,
}

export const brushToolConfig: ToolConfig = {
  name: ToolName.Brush,
  sub: false,
  toolOptions: [
    {
      id: 'brush',
      active: true,
      props: {
        emptyOnly: false,
      },
      category: 'brush-mode',
      action: 'brush_tool.activate_brush',
    },
    {
      id: 'eraser',
      active: false,
      props: {
        eraseAll: false,
      },
      category: 'brush-mode',
      action: 'brush_tool.activate_eraser',
    },
    {
      id: 'dimension_switcher',
      active: false,
      props: {
        dimension: MaskBrushDimension.Paint2D,
      },
      category: 'brush-dimension',
      action: '',
    },
    {
      id: 'round',
      active: true,
      props: {},
      category: 'brush-tip-shape',
      action: 'brush_tool.activate_round_tip',
    },
    {
      id: 'squared',
      active: false,
      props: {},
      category: 'brush-tip-shape',
      action: 'brush_tool.activate_squared_tip',
    },
    {
      id: 'size_and_threshold',
      active: false,
      props: {
        size: 10,
        threshold: {
          min: Number.MIN_SAFE_INTEGER,
          max: Number.MAX_SAFE_INTEGER,
        },
        isThresholdEnabled: false,
      },
      category: 'brush-size-and-threshold',
      action: '',
    },
    {
      id: 'brush-size',
      active: false,
      props: { value: 10, commandName: 'brush_tool.set_brush_size' },
      action: '',
    },
  ],
}

export const maskBrushToolConfig: ToolConfig = {
  name: ToolName.MaskBrush,
  sub: false,
  toolOptions: [
    {
      id: 'brush',
      active: true,
      props: {
        emptyOnly: false,
      },
      category: 'brush-mode',
      action: 'brush_tool.activate_brush',
    },
    {
      id: 'eraser',
      active: false,
      props: {
        eraseAll: false,
      },
      category: 'brush-mode',
      action: 'brush_tool.activate_eraser',
    },
    {
      id: 'dimension_switcher',
      active: false,
      props: {
        dimension: MaskBrushDimension.Paint2D,
      },
      category: 'brush-dimension',
      action: '',
    },
    {
      id: 'round',
      active: true,
      props: {},
      category: 'brush-tip-shape',
      action: 'brush_tool.activate_round_tip',
    },
    {
      id: 'squared',
      active: false,
      props: {},
      category: 'brush-tip-shape',
      action: 'brush_tool.activate_squared_tip',
    },
    {
      id: 'size_and_threshold',
      active: false,
      props: {
        size: 10,
        threshold: {
          min: Number.MIN_SAFE_INTEGER,
          max: Number.MAX_SAFE_INTEGER,
        },
        isThresholdEnabled: false,
      },
      category: 'brush-size-and-threshold',
      action: '',
    },
    {
      id: 'brush-size',
      active: false,
      props: { value: 10, commandName: 'brush_tool.set_brush_size' },
      action: '',
    },
  ],
}

export const fillToolConfig: ToolConfig = {
  name: ToolName.Fill,
  sub: false,
  toolOptions: [
    {
      id: 'fill_options',
      active: true,
      category: 'fill-options',
      action: '',
      props: {},
    },
  ],
}

export const ellipseToolConfig: ToolConfig = {
  name: ToolName.Ellipse,
  sub: false,
}

export const polylineToolConfig: ToolConfig = {
  name: ToolName.Polyline,
  sub: false,
}

export const clickerToolConfig: ToolConfig = {
  name: ToolName.Clicker,
  sub: false,
}

export const autoAnnotateToolConfig: SubToolConfig = {
  name: SubToolName.AutoAnnotate,
  sub: true,
}

export const emptyToolConfig: SubToolConfig = {
  name: SubToolName.Empty,
  sub: true,
}

export const windowLevelToolConfig: ToolConfig = {
  name: ToolName.WindowLevel,
  sub: false,
}

export const zoomToolConfig: ToolConfig = {
  name: ToolName.Zoom,
  sub: false,
}

export const commentatorToolConfig: ToolConfig = {
  name: ToolName.Commentator,
  sub: false,
}

export const directionalVectorToolConfig: SubToolConfig = {
  name: SubToolName.DirectionalVector,
  sub: true,
}

export const crosshairsToolConfig: ToolConfig = {
  name: ToolName.Crosshairs,
  sub: false,
}

export const skeletonToolConfig: ToolConfig = {
  name: ToolName.Skeleton,
  sub: false,
  toolOptions: [
    { id: 'restore_joints', active: false, props: {}, action: 'edit_tool.restore_joints' },
  ],
}

export const eyeToolConfig: ToolConfig = {
  name: ToolName.Eye,
  sub: false,
}

export const selectToolConfig: ToolConfig = {
  name: ToolName.Select,
  sub: false,
}

export const simpleTableToolConfig: ToolConfig = {
  name: ToolName.SimpleTable,
  sub: false,
  toolOptions: [
    { id: 'rows', active: false, props: { value: 2 }, action: '' },
    { id: 'cols', active: false, props: { value: 3 }, action: '' },
  ],
}
